import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CheckCircle,
  Error,
  AccessTime,
  Grade,
  TrendingUp,
  ArrowBack,
  ExpandMore,
  Lightbulb,
  TrendingFlat,
  CheckCircleOutline,
  ErrorOutline,
  Star,
  StarBorder,
  Edit,
  FormatQuote,
} from '@mui/icons-material';
import { writingResultsAPI } from '../services/apiServices';

const ResultPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [expandedPart, setExpandedPart] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const data = await writingResultsAPI.getResult(id);
        setResult(data.writingResult);
        setLoading(false);
      } catch (error) {
        toast.error(error.message);
        navigate('/');
      }
    };
    fetchResult();
  }, [id]);

  const handleQuestionExpand = (index) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };

  const handlePartExpand = (index) => {
    setExpandedPart(expandedPart === index ? null : index);
  };

  const renderErrorAnalysis = (errorTypes) => {
    if (!errorTypes) return null;
    
    return (
      <>
        {errorTypes.corrected && errorTypes.corrected.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="success.main">
              Corrected Errors:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {errorTypes.corrected.map((error, j) => (
                <Chip
                  key={j}
                  label={error}
                  size="small"
                  color="success"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        )}
        {errorTypes.missed && errorTypes.missed.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="error.main">
              Missed Errors:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {errorTypes.missed.map((error, j) => (
                <Chip
                  key={j}
                  label={error}
                  size="small"
                  color="error"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  };

  const renderSkillsAnalysis = (skillsAnalysis) => {
    if (!skillsAnalysis) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Skills Breakdown:
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(skillsAnalysis).map(([skill, score]) => (
            <Grid xs={6} sm={4} key={skill}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {skill.charAt(0).toUpperCase() + skill.slice(1)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={score * 20} // Convert 0-5 scale to percentage
                  sx={{ 
                    height: 8, 
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: score >= 4 ? 'success.main' : score >= 3 ? 'warning.main' : 'error.main'
                    }
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Add this new function to render learning paths
  const renderLearningPaths = () => {
    if (!result.recommendedLearningPaths || Object.keys(result.recommendedLearningPaths).length === 0) {
      return null;
    }

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Lightbulb sx={{ mr: 1 }} color="warning" /> Recommended Learning Paths
        </Typography>
        
        <Grid container spacing={2}>
          {Object.entries(result.recommendedLearningPaths).map(([path, reason], index) => {
            // Map learning path to app name and determine if it's available
            const appMap = {
              'grammar': { name: 'Grammarly', icon: <Edit />, available: true, appValue: 'grammar' },
              'writing': { name: 'Writely', icon: <Edit />, available: true, appValue: 'writing' },
              'spelling': { name: 'Spelling Pro', icon: <FormatQuote />, available: true, appValue: 'spelling' },
              'vocabulary': { name: 'Vocab Master', icon: <FormatQuote />, available: false },
              'reading': { name: 'Readly', icon: <FormatQuote />, available: true, url: 'https://readly.scholarlytraining.com/' },
              'speaking': { name: 'Speakify', icon: <FormatQuote />, available: true, url: 'https://speakify.scholarlytraining.com/' }
            };
            
            const app = appMap[path] || { 
              name: path.charAt(0).toUpperCase() + path.slice(1), 
              icon: <FormatQuote />, 
              available: false 
            };
            
            return (
              <Grid xs={12} sm={6} md={4} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderTop: '3px solid',
                  borderColor: 'warning.main'
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ mr: 1, color: 'warning.main' }}>
                        {app.icon}
                      </Box>
                      <Typography variant="h6">{app.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {reason}
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      disabled={!app.available}
                      fullWidth
                      onClick={() => {
                        if (app.url) {
                          window.location.href = app.url;
                        } else {
                          localStorage.setItem('selectedApp', app.appValue);
                          window.location.href = '/';
                        }
                      }}
                    >
                      {app.available ? 'Start Learning' : 'Coming Soon'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  // Render creative/persuasive writing results
  const renderCreativePersuasiveResults = () => {
    const evaluation = result.creativePersuasiveEvaluation;
    const testType = result.testSession.testType === 'creative_writing' ? 'Creative Writing' : 'Persuasive Writing';
    
    return (
      <Grid container spacing={3}>
        {/* Summary Section */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              {testType} Evaluation
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                icon={<TrendingUp />}
                label={`Overall Score: ${evaluation.overallScore}/50`}
                color="primary"
              />
              <Chip
                icon={<AccessTime />}
                label={`Time: ${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`}
              />
              <Chip
                icon={<Grade />}
                label={`Grade Level: ${result.recommendedGrade}`}
                color="secondary"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Add Learning Paths Section */}
        <Grid xs={12}>
          {renderLearningPaths()}
        </Grid>

        {/* Selected Parts Analysis */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              {/* Detailed Feedback */}
              Section 1
            </Typography>
            
            {evaluation.selectedParts.map((part, index) => (
              <Accordion 
                key={index}
                expanded={expandedPart === index}
                onChange={() => handlePartExpand(index)}
                sx={{ 
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore />}
                  sx={{ 
                    bgcolor: 'grey.50',
                    borderBottom: expandedPart === index ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="h6">
                    #{part.partNumber}
                    {/* Part {part.partNumber} */}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                      Selected Text:
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        fontStyle: 'italic'
                      }}
                    >
                      <FormatQuote color="primary" sx={{ fontSize: 20, verticalAlign: 'top', mr: 1 }} />
                      {part.text}
                    </Paper>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Strengths */}
                    <Grid xs={12} md={6}>
                      <Card sx={{ height: '100%', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
                        <CardContent>
                          <Typography variant="h6" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <Star sx={{ mr: 1 }} color="success" /> Strengths
                          </Typography>
                          <List dense>
                            {part.strengths.map((strength, idx) => (
                              <ListItem key={idx} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <CheckCircle color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={<Typography variant="subtitle2">{strength.name}</Typography>}
                                  secondary={strength.description}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Weaknesses */}
                    <Grid xs={12} md={6}>
                      <Card sx={{ height: '100%', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light' }}>
                        <CardContent>
                          <Typography variant="h6" color="warning.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarBorder sx={{ mr: 1 }} color="warning" /> Areas for Improvement
                          </Typography>
                          <List dense>
                            {part.weaknesses.map((weakness, idx) => (
                              <ListItem key={idx} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <ErrorOutline color="warning" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={<Typography variant="subtitle2">{weakness.name}</Typography>}
                                  secondary={weakness.description}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Add exemplar section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                      Suggested Improvement:
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'info.50', 
                        borderLeft: '4px solid',
                        borderColor: 'info.main',
                      }}
                    >
                      <Typography sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                        {part.exemplar}
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>

        {/* Overall Critique */}
        <Grid xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Overall Comments
              </Typography>
              <Typography paragraph sx={{ whiteSpace: 'pre-line' }}>
                {evaluation.overallCritique}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Revision Guidelines */}
        <Grid xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Edit sx={{ mr: 1 }} /> Section 2
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Original Text:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {evaluation.revisionGuidelines.originalText}
                </Typography>
              </Paper>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Revised Text (with suggestions):
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: 'info.50',
                  border: '1px solid',
                  borderColor: 'info.light',
                  '& .mistake': {
                    textDecoration: 'line-through',
                    color: 'error.main',
                    mr: 0.5
                  }
                }}
              >
                <Typography 
                  sx={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{ 
                    __html: evaluation.revisionGuidelines.revisedText 
                  }}
                />
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Render standard writing results
  const renderStandardResults = () => {
    return (
      <Grid container spacing={3}>
        {/* Summary Section */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Test Results Summary
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                icon={<TrendingUp />}
                label={`Overall Score: ${Math.round(result.totalScore * 100)}%`}
                color="primary"
              />
              <Chip
                icon={<AccessTime />}
                label={`Time: ${Math.floor(result.timeTaken / 60)}m ${result.timeTaken % 60}s`}
              />
              <Chip
                icon={<Grade />}
                label={`Recommended Grade: ${result.recommendedGrade}`}
                color="secondary"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Add Learning Paths Section */}
        <Grid xs={12}>
          {renderLearningPaths()}
        </Grid>

        {/* Skills Distribution Table */}
        <Grid xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Skills Distribution
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Skill</TableCell>
                    <TableCell align="center">Questions Asked</TableCell>
                    <TableCell align="center">Questions Answered</TableCell>
                    <TableCell align="center">Average Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.skillDistribution
                    ?.sort((a, b) => b.averageScore - a.averageScore)
                    .map((skill) => (
                      <TableRow key={skill.name}>
                        <TableCell>{skill.name}</TableCell>
                        <TableCell align="center">{skill.questionsAsked}</TableCell>
                        <TableCell align="center">{skill.questionsAnswered}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Typography>
                              {Math.round(skill.averageScore * 100)}%
                            </Typography>
                            <Box
                              sx={{
                                width: 60,
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                                height: 8,
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${skill.averageScore * 100}%`,
                                  bgcolor: skill.averageScore > 0.7 
                                    ? 'success.main' 
                                    : skill.averageScore > 0.4 
                                      ? 'warning.main' 
                                      : 'error.main',
                                  height: '100%',
                                  borderRadius: 1,
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>

        {/* Comprehensive Feedback Section */}
        <Grid xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                AI Feedback Summary
              </Typography>
              <Typography paragraph sx={{ whiteSpace: 'pre-line' }}>
                {result.comprehensiveFeedback.feedback}
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 3 }}>
                Learning Strategies
              </Typography>
              <List>
                {result.comprehensiveFeedback.learningStrategies.map((strategy, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Lightbulb color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={strategy} />
                  </ListItem>
                ))}
              </List>

              {result.comprehensiveFeedback.recommendedNextSteps && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Next Steps
                  </Typography>
                  <List>
                    {result.comprehensiveFeedback.recommendedNextSteps.map((step, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <TrendingFlat color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Strengths and Weaknesses */}
        {(result.strengthAreas && result.strengthAreas.length > 0) && (
          <Grid xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Strengths
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {result.strengthAreas.map((area, index) => (
                    <Chip
                      key={index}
                      icon={<CheckCircle />}
                      label={area}
                      color="success"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {(result.weaknessAreas && result.weaknessAreas.length > 0) && (
          <Grid xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Areas for Improvement
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {result.weaknessAreas.map((area, index) => (
                    <Chip
                      key={index}
                      icon={<Error />}
                      label={area}
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Detailed Question Review */}
        <Grid xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Detailed Question Analysis
            </Typography>
            {result.answers.map((answer, index) => (
              <Accordion
                key={index}
                expanded={expandedQuestion === index}
                onChange={() => handleQuestionExpand(index)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Typography variant="h6">
                      Question {index + 1}
                    </Typography>
                    <Chip
                      icon={answer.isCorrect ? <CheckCircleOutline /> : <ErrorOutline />}
                      label={`Score: ${Math.round(answer.score * 100)}%`}
                      color={answer.score > 0.7 ? 'success' : answer.score > 0.4 ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                      <Typography variant="subtitle1" color="textSecondary">
                        Original Text:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        {result.testSession.questions[index].question}
                      </Paper>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <Typography variant="subtitle1" color="textSecondary">
                        Your Answer:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        {answer.userAnswer}
                      </Paper>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
                    Feedback:
                  </Typography>
                  <Typography paragraph>
                    {answer.feedback}
                  </Typography>

                  {renderErrorAnalysis(answer.errorTypes)}
                  {renderSkillsAnalysis(answer.skillsAnalysis)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  // Determine if this is a creative/persuasive writing result
  const isCreativePersuasive = result.creativePersuasiveEvaluation && 
    (result.testSession.testType === 'creative_writing' || 
     result.testSession.testType === 'persuasive_writing');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      {isCreativePersuasive ? renderCreativePersuasiveResults() : renderStandardResults()}
    </Container>
  );
};

export default ResultPage; 